/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useMemo } from 'react';
import type { OptionProps, OptionTypeBase } from 'react-select';
import { components } from 'react-select';
import { useI18n } from '@whizz/react-i18n';
import type { DateRangeISOType, DateRangeType } from '../../forms/types';
import type { DateRangeOptionsType } from '../../pages/types';
import { date_range } from '../../pages/types';
import type { ToggleModalProps } from '../../components/modal/types';
import type { DateRangeModalProps } from '../../global/modals/DateRangeModal';
import { useModal } from '../useModal';
import { Dropdown } from '../../components';
import { CustomOptionWithIcon } from '../../components/selector/components';
import { DateRangeModal } from '../../global';
import { getDateStringWithLocale } from '../../util/dates';
import { GLOBAL_ICONS, LAST_WEEKS } from '../../util/vars';
import { useLocalStorage } from 'react-use';
import { DateTime } from 'luxon';

const custom_id = 'custom';

type OptionsProp = {
  defaultValue?: string | null;
  showLabel?: string;
  maxDate?: Date | null | undefined;
  customOptions?: DateRangeOptionsType[];
  label?: string;
};

export const useDateRangeSelector = (
  options?: OptionsProp,
): [JSX.Element, { value: DateRangeOptionsType, label: string } | null, DateRangeType] => {
  const { translate, locale } = useI18n();

  const maxDate = options?.maxDate;
  const showLabel = options?.showLabel;
  const customLabel = options?.label ?? null;
  const [
    lastSessionDateRange,
    setLastSessionDateRange,
  ] = useLocalStorage<{ value: { value: DateRangeOptionsType, label: string } | null, customValue: DateRangeISOType } | null>('last_session_date_range', null);

  const getOptions = (): { value: DateRangeOptionsType, label: string }[] => {
    return (options?.customOptions ?? date_range).map((range) => {
      return {
        value: range,
        label: translate(`settings.DATE_RANGE.OPTIONS.${range}`, { weeks: LAST_WEEKS }),
      };
    });
  };

  const getValue = (): { value: DateRangeOptionsType, label: string } => {
    const selectOptions = getOptions();
    return selectOptions.find((option) => lastSessionDateRange?.value?.value === option.value) ?? selectOptions.find((option) => options?.defaultValue === option.value) ?? selectOptions[0];
  };

  const getCustomValue = () => {
    return ({
      startDate: null != lastSessionDateRange?.customValue.startDate
        ? DateTime.fromISO(lastSessionDateRange.customValue.startDate).toJSDate()
        : null,
      endDate: null != lastSessionDateRange?.customValue.endDate
        ? DateTime.fromISO(lastSessionDateRange.customValue.endDate).toJSDate()
        : null,
    });
  };

  const value = getValue();
  const customValue = useMemo(getCustomValue, [ lastSessionDateRange ]);

  const [ modal, toggle, setProps ] = useModal((props: DateRangeModalProps & Pick<ToggleModalProps, 'toggle'>) => (
    <DateRangeModal {...props} />
  ), {
    title: translate('settings.DATE_RANGE.MODAL.title'),
    size: 'sm',
    onSubmit: (values: DateRangeType) => {
      const newCustomValue = { startDate: values.startDate, endDate: values.endDate };

      setLastSessionDateRange({
        value: getOptions().find((option) => option.value === custom_id)!,
        customValue: {
          startDate: null != newCustomValue.startDate
            ? DateTime.fromJSDate(newCustomValue.startDate).toUTC().toISO()
            : null,
          endDate: null != newCustomValue.endDate
            ? DateTime.fromJSDate(newCustomValue.endDate).toUTC().toISO()
            : null,
        },
      });
    },
  });

  const getCustomRangeString = (): string | null => {
    if (customValue?.startDate && customValue?.endDate) {
      return `${getDateStringWithLocale(customValue.startDate, locale?.id)}-${getDateStringWithLocale(customValue.endDate, locale?.id)}`;
    }

    return null;
  };

  useEffect(() => {
    setProps((oldProps: DateRangeModalProps & Pick<ToggleModalProps, 'toggle'>) => ({
      ...oldProps,
      initialValues: customValue,
      maxDate: maxDate,
    }));
  }, [ customValue ]);

  const selectorComponent = (
    <>
      {modal}
      <div className={'flex mx-auto'}>
        <div className={'w-full'}>
          <Dropdown
            menuIsOpen
            id={'date_range_selector'}
            className={'mx-auto'}
            align={'space-between'}
            label={customLabel ?? 'Time range'}
            hideLabel={!customLabel}
            disableSearch={true}
            icon={GLOBAL_ICONS.calendar}
            buttonPlaceholder={translate('components.SELECTORS.PLACEHOLDERS.DATE_RANGE')}
            options={getOptions()}
            value={showLabel ? { value: value?.value, label: showLabel } : value}
            customValueToShow={getCustomRangeString()}
            handleChange={(option: { value: DateRangeOptionsType, label: string }) => {

              if (option.value === custom_id) {
                toggle(true);
              } else {

                setLastSessionDateRange({
                  value: option,
                  customValue: {
                    startDate: null,
                    endDate: null,
                  },
                });
              }
            }}
            componentsForSelect={{
              Option: (props: OptionProps<OptionTypeBase, false>) => {
                if (props.data.value === custom_id) {
                  return (
                    <CustomOptionWithIcon
                      icon={GLOBAL_ICONS.open_modal}
                      {...props}
                    >
                      {props.children}
                      {getCustomRangeString() !== null ? (
                        <div className={'text-sm'}>
                          {getCustomRangeString()}
                        </div>
                      ) : '...'}
                    </CustomOptionWithIcon>
                  );
                }
                return (
                  <components.Option {...props} />
                );
              },
            }}
          />
        </div>
      </div>
    </>
  );

  return [ selectorComponent, value, customValue ];
};
